import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { PreviewCard } from "./ResumeVersions";

const TONES = ["Professional", "Confident", "Friendly", "Senior", "Direct"];

export default function CoverLetter() {
  const [resumes, setResumes] = useState([]);
  const [form, setForm] = useState({ resume_id: "", job_title: "", company: "", job_description: "", tone: "Professional" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/resumes").then((r) => {
      setResumes(r.data || []);
      if (r.data?.[0]) setForm((f) => ({ ...f, resume_id: r.data[0].resume_id }));
    });
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.job_title || !form.company || !form.job_description) return toast.error("Fill all fields");
    setBusy(true);
    try {
      const r = await api.post("/cover-letter", form);
      setData(r.data);
      toast.success("Cover letter ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">Cover letter</p>
        <h1 className="font-display font-black text-4xl tracking-tight mt-1">Write the perfect opener</h1>
        <p className="text-slate-500 mt-2 max-w-xl">Five tones — Professional, Confident, Friendly, Senior, Direct.</p>
      </div>

      <form onSubmit={submit} className="glass p-6 fade-up-delay-1 grid lg:grid-cols-2 gap-5">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Resume (optional)</span>
          <select
            data-testid="cl-resume"
            value={form.resume_id}
            onChange={(e) => setForm({ ...form, resume_id: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-white/10 focus:border-[#00E5FF] outline-none px-3 py-2.5 text-sm bg-white/5 text-white"
          >
            <option value="">— None —</option>
            {resumes.map((r) => <option key={r.resume_id} value={r.resume_id}>{r.filename}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Tone</span>
          <select
            data-testid="cl-tone"
            value={form.tone}
            onChange={(e) => setForm({ ...form, tone: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-white/10 focus:border-[#00E5FF] outline-none px-3 py-2.5 text-sm bg-white/5 text-white"
          >
            {TONES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
        <Field label="Job title" value={form.job_title} onChange={(v) => setForm({ ...form, job_title: v })} testid="cl-title" />
        <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="cl-company" />
        <div className="lg:col-span-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Job description</span>
          <textarea
            data-testid="cl-jd"
            value={form.job_description}
            onChange={(e) => setForm({ ...form, job_description: e.target.value })}
            rows={8}
            className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-3 text-sm font-mono"
          />
        </div>
        <div className="lg:col-span-2 flex justify-end">
          <button
            data-testid="cl-submit"
            type="submit"
            disabled={busy}
            className="btn-neon inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Mail size={16} /> {busy ? "Writing…" : "Generate cover letter"}
          </button>
        </div>
      </form>

      {data && (
        <div className="fade-up-delay-2">
          <PreviewCard
            testid="cl-preview"
            title={`Cover Letter — ${form.company}`}
            md={data.cover_letter_markdown || ""}
            filename={`cavilo-cl-${form.company || "letter"}`}
          />
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm"
      />
    </label>
  );
}
